import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { addIcons } from 'ionicons';
import { addOutline, arrowBackOutline, cartOutline, checkmarkCircleOutline, refreshOutline, removeOutline, storefrontOutline } from 'ionicons/icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommerceOrderStatus, PublicShop, PublicShopGroup, PublicShopItem, PublicStorefront, ShopCartLine, ShopLocalizedText } from './shop.models';
import { ShopService } from './shop.service';

type ShopStep = 'catalogue' | 'checkout' | 'payment' | 'result';

/** Validates that both checkout email fields contain the same trimmed value. */
function matchingCheckoutEmails(control: AbstractControl): ValidationErrors | null {
  const email = String(control.get('email')?.value || '').trim();
  const emailConfirmation = String(control.get('emailConfirmation')?.value || '').trim();
  if (!email || !emailConfirmation)
    return null;
  return email === emailConfirmation ? null : { emailMismatch: true };
}

@Component({
  selector: 'app-shop',
  templateUrl: './shop.page.html',
  styleUrls: ['./shop.page.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, IonicModule, TranslateModule]
})
export class ShopPage implements OnInit {
  storefrontId = '';
  requestedShopId = '';
  selectedLanguage = 'en';
  storefront: PublicStorefront | null = null;
  selectedShopId = '';
  quantities: Record<string, number> = {};
  cartLines: ShopCartLine[] = [];
  step: ShopStep = 'catalogue';
  isLoading = true;
  isSubmitting = false;
  isCheckingPayment = false;
  errorMessage = '';
  orderStatus: CommerceOrderStatus | null = null;
  checkoutForm: FormGroup;

  constructor(
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private shopService: ShopService,
    private translateService: TranslateService
  ) {
    addIcons({ addOutline, arrowBackOutline, cartOutline, checkmarkCircleOutline, refreshOutline, removeOutline, storefrontOutline });
    this.checkoutForm = this.formBuilder.group({
      firstName: ['', [Validators.required, Validators.maxLength(100)]],
      lastName: ['', [Validators.required, Validators.maxLength(100)]],
      email: ['', [Validators.required, Validators.email, Validators.maxLength(200)]],
      emailConfirmation: ['', [Validators.required, Validators.email, Validators.maxLength(200)]]
    }, { validators: matchingCheckoutEmails });
  }

  /** Loads the storefront and resumes a payment result when redirect parameters are present. */
  async ngOnInit(): Promise<void> {
    this.storefrontId = this.route.snapshot.queryParamMap.get('storefrontId')?.trim() || '';
    this.requestedShopId = this.route.snapshot.queryParamMap.get('shopId')?.trim() || '';
    this.selectedLanguage = this.route.snapshot.queryParamMap.get('lang')?.trim() || navigator.language.split('-')[0] || 'en';
    const transactionToken = this.route.snapshot.queryParamMap.get('commerceToken')?.trim() || '';
    const redirectStatus = this.route.snapshot.queryParamMap.get('commercePaymentStatus')?.trim() || '';

    if (!this.storefrontId) {
      this.errorMessage = 'This shop link is incomplete.';
      this.isLoading = false;
      return;
    }

    try {
      await this.shopService.ensureAnonymousAuthentication();
      this.storefront = await this.shopService.getStorefront(this.storefrontId, this.requestedShopId);
      this.normalizeLanguage();
      this.translateService.use(this.selectedLanguage);
      this.selectedShopId = this.storefront.shops[0]?.id || '';
      if (transactionToken) {
        this.step = 'payment';
        await this.refreshPaymentStatus(transactionToken, redirectStatus === 'redirected');
      }
    } catch (error) {
      this.errorMessage = this.getErrorMessage(error);
    } finally {
      this.isLoading = false;
    }
  }

  /** Returns the shop currently selected by the segmented navigation. */
  get selectedShop(): PublicShop | null {
    return this.storefront?.shops.find((shop) => shop.id === this.selectedShopId) || null;
  }

  /** Returns the non-empty visible groups for one shop. */
  getGroups(shop: PublicShop): PublicShopGroup[] {
    return shop.groups.filter((group) => this.getItems(shop, group.id).length > 0);
  }

  /** Returns the ordered items belonging to one group. */
  getItems(shop: PublicShop, groupId: string): PublicShopItem[] {
    return shop.items.filter((item) => item.groupId === groupId);
  }

  /** Returns a stable map key for a nested shop item. */
  getItemKey(item: PublicShopItem): string {
    return `${item.shopId}:${item.id}`;
  }

  /** Returns the current quantity of an item in the cross-shop cart. */
  getQuantity(item: PublicShopItem): number {
    return this.quantities[this.getItemKey(item)] || 0;
  }

  /** Changes an item quantity while respecting stock and per-order limits. */
  changeQuantity(item: PublicShopItem, delta: number): void {
    const key = this.getItemKey(item);
    const current = this.getQuantity(item);
    const stockLimit = item.remaining === null ? item.maxPerOrder : Math.min(item.maxPerOrder, item.remaining);
    const next = Math.min(stockLimit, Math.max(0, current + delta));
    if (next === 0)
      delete this.quantities[key];
    else
      this.quantities[key] = next;
    this.synchronizeCartLines();
  }

  /** Indicates whether one more unit can be added to the cart. */
  canIncrement(item: PublicShopItem): boolean {
    const limit = item.remaining === null ? item.maxPerOrder : Math.min(item.maxPerOrder, item.remaining);
    return this.getQuantity(item) < limit;
  }

  /** Returns the total number of product units in the cart. */
  get cartQuantity(): number {
    return this.cartLines.reduce((total, line) => total + line.quantity, 0);
  }

  /** Returns the product subtotal before reusable-container deposits. */
  get productSubtotal(): number {
    return this.cartLines.reduce((total, line) => total + line.item.price * line.quantity, 0);
  }

  /** Returns deposits that are automatically included in the order. */
  get depositSubtotal(): number {
    return this.cartLines.reduce((total, line) => total + line.item.depositAmount * line.quantity, 0);
  }

  /** Returns the amount that will be sent to the payment provider. */
  get cartTotal(): number {
    return this.productSubtotal + this.depositSubtotal;
  }

  /** Opens the existing form-style checkout summary. */
  openCheckout(): void {
    if (this.cartQuantity > 0) {
      this.errorMessage = '';
      this.step = 'checkout';
    }
  }

  /** Returns from checkout without discarding the cross-shop cart. */
  returnToCatalogue(): void {
    this.errorMessage = '';
    this.step = 'catalogue';
  }

  /** Prevents pasting into the confirmation field so the address is entered twice. */
  preventEmailConfirmationPaste(event: ClipboardEvent): void {
    event.preventDefault();
  }

  /** Reserves stock and redirects the customer to the single Mollie checkout. */
  async submitOrder(): Promise<void> {
    if (this.checkoutForm.invalid || this.cartLines.length === 0 || this.isSubmitting) {
      this.checkoutForm.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.step = 'payment';
    try {
      const reservation = await this.shopService.reserveOrder({
        storefrontId: this.storefrontId,
        ...(this.requestedShopId ? { shopId: this.requestedShopId } : {}),
        language: this.selectedLanguage,
        customer: this.checkoutForm.getRawValue(),
        cart: this.cartLines.map((line) => ({ shopId: line.shop.id, itemId: line.item.id, quantity: line.quantity }))
      });
      const payment = await this.shopService.startPayment(this.storefrontId, reservation.orderId, window.location.href);
      if (payment.completed) {
        await this.refreshPaymentStatus(payment.transactionToken, false);
        return;
      }
      if (!payment.checkoutURL)
        throw new Error('The payment page is unavailable.');
      window.location.assign(payment.checkoutURL);
    } catch (error) {
      this.errorMessage = this.getErrorMessage(error);
      this.isSubmitting = false;
      this.step = 'checkout';
    }
  }

  /** Refreshes a redirected order and briefly polls while its webhook is being processed. */
  async refreshPaymentStatus(transactionToken?: string, poll = false): Promise<void> {
    const token = transactionToken || this.route.snapshot.queryParamMap.get('commerceToken')?.trim() || '';
    if (!token || this.isCheckingPayment)
      return;
    this.isCheckingPayment = true;
    this.errorMessage = '';
    try {
      const attempts = poll ? 8 : 1;
      for (let index = 0; index < attempts; index++) {
        this.orderStatus = await this.shopService.getOrderStatus(this.storefrontId, token);
        if (['paid', 'expired', 'cancelled'].includes(this.orderStatus.status))
          break;
        if (index < attempts - 1)
          await this.delay(1500);
      }
      this.step = 'result';
    } catch (error) {
      this.errorMessage = this.getErrorMessage(error);
      this.step = 'result';
    } finally {
      this.isCheckingPayment = false;
      this.isSubmitting = false;
    }
  }

  /** Returns a localized catalogue value with predictable fallbacks. */
  localize(value: ShopLocalizedText | undefined): string {
    if (!value)
      return '';
    return value[this.selectedLanguage]
      || value[this.storefront?.event.defaultLanguage || '']
      || Object.values(value).find((entry) => !!entry)
      || '';
  }

  /** Formats money in the selected language and storefront currency. */
  formatAmount(value: number): string {
    return new Intl.NumberFormat(this.selectedLanguage, {
      style: 'currency',
      currency: this.storefront?.currency || 'EUR'
    }).format(value || 0);
  }

  /** Switches storefront language without changing its public identifiers. */
  setLanguage(language: string): void {
    this.selectedLanguage = language;
    this.translateService.use(language);
    const url = new URL(window.location.href);
    url.searchParams.set('lang', language);
    window.history.replaceState({}, '', url.toString());
  }

  /** Selects a shop while retaining items already chosen from other shops. */
  selectShop(event: CustomEvent): void {
    this.selectedShopId = String(event.detail.value || '');
  }

  /** Tracks nested catalogue rows without coupling them to array indexes. */
  trackById(_index: number, value: { id: string }): string {
    return value.id;
  }

  /** Tracks cart rows by their stable nested shop item identifier. */
  trackByCartLine(_index: number, line: ShopCartLine): string {
    return `${line.shop.id}:${line.item.id}`;
  }

  /** Removes payment-return parameters and starts a fresh cart on the same storefront. */
  startNewOrder(): void {
    const url = new URL(window.location.href);
    url.searchParams.delete('commercePaymentStatus');
    url.searchParams.delete('commerceToken');
    window.location.assign(url.toString());
  }

  /** Keeps the current language inside the event's configured language set. */
  private normalizeLanguage(): void {
    const languages = this.storefront?.event.communicationLanguages || ['en'];
    if (!languages.includes(this.selectedLanguage))
      this.selectedLanguage = this.storefront?.event.defaultLanguage || languages[0] || 'en';
  }

  /** Rebuilds the cart only after a quantity change to keep Angular list identities stable. */
  private synchronizeCartLines(): void {
    if (!this.storefront) {
      this.cartLines = [];
      return;
    }

    const lines: ShopCartLine[] = [];
    this.storefront.shops.forEach((shop) => shop.items.forEach((item) => {
      const quantity = this.getQuantity(item);
      if (quantity > 0)
        lines.push({ shop, item, quantity });
    }));
    this.cartLines = lines;
  }

  /** Converts callable errors into a concise customer-facing message. */
  private getErrorMessage(error: unknown): string {
    const message = error && typeof error === 'object' && 'message' in error ? String((error as { message: unknown }).message) : String(error || '');
    return message.replace(/^Firebase:\s*/i, '').replace(/^functions\/[\w-]+:\s*/i, '') || 'The shop is temporarily unavailable. Please try again.';
  }

  /** Waits between short payment-status polling attempts. */
  private delay(milliseconds: number): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
  }
}
